import ThumbnailWorker from '../workers/thumbnail.worker.ts?worker'
import { galleryOwnerPath } from './galleryOwnerSegment'

interface S3UploadResponse {
  url: string
  thumbnailUrl?: string
  key: string
  metadata: {
    userId: string
    projectId: string
    sessionId: string
    eventId?: string
    createdAt: string
    originalFilename: string
    contentType: string
    size: string
    width?: string
    height?: string
  }
}

class S3Utils {
  public static async getImageDimensions(
    file: File
  ): Promise<{ width: number; height: number } | null> {
    if (!file.type.startsWith('image/')) return null

    return new Promise((resolve) => {
      const img = new Image()
      img.onload = (): void => {
        resolve({ width: img.width, height: img.height })
      }
      img.onerror = (): void => resolve(null)
      img.src = URL.createObjectURL(file)
    })
  }

  private static createThumbnail(file: File): Promise<Blob | null> {
    if (!file.type.startsWith('image/') || typeof Worker === 'undefined') {
      return Promise.resolve(null)
    }

    return new Promise((resolve) => {
      const worker = new ThumbnailWorker()

      worker.onmessage = (event: MessageEvent<{ thumbnailBlob: Blob | null }>): void => {
        resolve(event.data.thumbnailBlob)
        worker.terminate()
      }

      worker.onerror = (error): void => {
        console.error('Thumbnail worker error:', error)
        resolve(null)
        worker.terminate()
      }

      worker.postMessage({ file })
    })
  }

  /**
   * The licence pair /api/upload accepts when the session cookie is missing or
   * expired (resolveSubscriptionSession needs BOTH). The main-process upload jobs
   * and the renderer's /api/gallery call already send it; this call sent the
   * cookie alone, so a booth whose cookie lapsed got a 401 for every photo and
   * thumbnail until the main-process retry uploaded it: ~300k rejections a week,
   * all during opening hours. Loaded lazily, like uploadManager does, to keep
   * this util free of a store import cycle.
   */
  private static async subscriptionAuthFields(): Promise<{ token?: string; deviceId?: string }> {
    try {
      const { useSettingsStore } = await import('@renderer/stores/settingsStore')
      const { activationCode, deviceId } = useSettingsStore.getState()
      return activationCode && deviceId ? { token: activationCode, deviceId } : {}
    } catch {
      return {}
    }
  }

  public static async uploadFile(
    file: File,
    userId: string,
    projectId: string,
    sessionId: string,
    galleryId: string, // this is timestamp-sessionId
    eventId?: string
  ): Promise<S3UploadResponse> {
    try {
      // Ensure we have a valid content type
      const contentType = file.type || 'image/jpeg'

      const dimensions = await this.getImageDimensions(file)
      const reversedTimestamp = 9999999999999 - Date.now()
      // Use hardcoded projectId for bahela users without a project (same as uploadManager)
      const BAHELA_DEFAULT_PROJECT_ID = '691d305ea5b85a88793907bb'
      const normalizedProjectId = projectId && projectId.trim() !== '' ? projectId : BAHELA_DEFAULT_PROJECT_ID
      // Use normalized projectId in S3 path (consistent with metadata)
      const projectPath = normalizedProjectId
      // The owner segment is a hash, not the address: a gallery key is the URL
      // an event guest is handed. See utils/galleryOwnerSegment.ts.
      const owner = await galleryOwnerPath(userId)
      const filename = `gallery/${owner}/${projectPath}/${galleryId}/${reversedTimestamp}-${file.name}`

      const metadata = {
        uploadType: 'gallery',
        userId,
        projectId: normalizedProjectId,
        sessionId,
        eventId,
        createdAt: new Date().toISOString(),
        originalFilename: file.name,
        contentType,
        size: file.size.toString(),
        ...(dimensions && {
          width: dimensions.width.toString(),
          height: dimensions.height.toString()
        })
      }

      const authFields = await this.subscriptionAuthFields()

      // Get signed URL from API
      const response = await fetch(`${process.env.VITE_API_URL}/api/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          filename,
          contentType,
          metadata,
          ...authFields
        })
      })

      if (!response.ok) {
        throw new Error('Failed to get upload URL')
      }

      const { uploadUrl, fileUrl } = await response.json()

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType }
      })

      if (!uploadResponse.ok) {
        throw new Error('Upload failed')
      }

      let thumbnailUrl: string | undefined

      // Create and upload thumbnail for images
      if (file.type.startsWith('image/')) {
        const thumbnailBlob = await this.createThumbnail(file)
        if (thumbnailBlob) {
          const thumbnailFilename = `gallery/${owner}/${normalizedProjectId}/${galleryId}/${reversedTimestamp}-thumb-${file.name}`
          const thumbnailFile = new File([thumbnailBlob], `thumb-${file.name}`, { type: file.type })

          const thumbMetadata = { ...metadata, originalFilename: thumbnailFile.name }

          // Get signed URL for thumbnail
          const thumbResponse = await fetch(`${process.env.VITE_API_URL}/api/upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              filename: thumbnailFilename,
              contentType: file.type,
              metadata: thumbMetadata,
              ...authFields
            })
          })

          if (thumbResponse.ok) {
            const { uploadUrl: thumbUploadUrl, fileUrl: thumbFileUrl } = await thumbResponse.json()

            const thumbUploadResponse = await fetch(thumbUploadUrl, {
              method: 'PUT',
              body: thumbnailFile,
              headers: { 'Content-Type': file.type }
            })

            if (thumbUploadResponse.ok) {
              thumbnailUrl = thumbFileUrl
            } else {
              console.warn('Thumbnail upload failed')
            }
          }
        }
      }

      return {
        url: fileUrl,
        key: filename,
        metadata,
        thumbnailUrl
      }
    } catch (error: unknown) {
      throw new Error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }
}

export default S3Utils
